import React, { useEffect, useState } from "react";
import { api, apiErr, fmtDate } from "../lib/api";
import { ASSIGNABLE_ROLES, ROLE_LABELS } from "../lib/permissions";
import { PageHeader } from "../components/ResourcePage";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Bell, Check, X, UserCog, Ban, RotateCcw, UserPlus, KeyRound, Lock, Unlock, Copy } from "lucide-react";
import { toast } from "sonner";

const isLocked = (u) => u.lock_until && new Date(u.lock_until) > new Date();

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  deactivated: "bg-slate-200 text-slate-500",
};

export function Admin() {
  const [users, setUsers] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [approveFor, setApproveFor] = useState(null);
  const [roleSel, setRoleSel] = useState("field_worker");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", phone: "", role: "field_worker" });
  const [creating, setCreating] = useState(false);
  const [tempResult, setTempResult] = useState(null); // {name, email, employee_id, temp_password}

  const load = async () => {
    try {
      const [u, n] = await Promise.all([api.get("/admin/users"), api.get("/admin/notifications")]);
      setUsers(u.data); setNotifs(n.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (fn) => { try { await fn(); toast.success("Done"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const openApprove = (u) => { setApproveFor(u); setRoleSel(u.role_requested || "field_worker"); };
  const confirmApprove = () => act(async () => {
    await api.post(`/admin/users/${approveFor.user_id}/approve`, { role: roleSel });
    setApproveFor(null);
  });

  const createEmployee = async () => {
    if (!createForm.name || !createForm.email) return toast.error("Name and email are required.");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/users", createForm);
      setShowCreate(false);
      setCreateForm({ name: "", email: "", phone: "", role: "field_worker" });
      setTempResult({ name: createForm.name, email: createForm.email, ...data });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setCreating(false); }
  };

  const resetPwd = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/reset-password`);
      setTempResult({ name: u.name, email: u.email, employee_id: u.employee_id, temp_password: data.temp_password });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const markRead = () => act(() => api.post("/admin/notifications/read"));

  const pending = users.filter((u) => u.status === "pending");

  const UserTable = ({ rows }) => (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Employee ID</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Requested</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">No users here.</TableCell></TableRow>
          ) : rows.map((u) => (
            <TableRow key={u.user_id} data-testid="admin-user-row">
              <TableCell>
                <div className="font-semibold">{u.name}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </TableCell>
              <TableCell className="text-sm">{u.employee_id || "—"}</TableCell>
              <TableCell className="text-sm">{u.phone || "—"}</TableCell>
              <TableCell className="text-sm capitalize">{(u.role_requested || "—").replace(/_/g, " ")}</TableCell>
              <TableCell>
                <Select value={u.role} onValueChange={(v) => act(() => api.post(`/admin/users/${u.user_id}/role`, { role: v }))}>
                  <SelectTrigger className="h-8 w-36" data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Badge className={`${STATUS_STYLE[u.status] || ""} border-0 capitalize`}>{u.status}</Badge>
                {isLocked(u) && (
                  <Badge className="ml-1 border-0 bg-red-100 text-red-700" data-testid="admin-locked-badge">
                    <Lock className="mr-1 h-3 w-3" />Locked
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex flex-wrap justify-end gap-1">
                  {u.status === "pending" && (
                    <>
                      <Button size="sm" className="h-8" onClick={() => openApprove(u)} data-testid="admin-approve-btn"><Check className="mr-1 h-3.5 w-3.5" />Approve</Button>
                      <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={() => act(() => api.post(`/admin/users/${u.user_id}/reject`))} data-testid="admin-reject-btn"><X className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                  {isLocked(u) && (
                    <Button size="sm" variant="outline" className="h-8" onClick={() => act(() => api.post(`/admin/users/${u.user_id}/unlock`))} data-testid="admin-unlock-btn"><Unlock className="mr-1 h-3.5 w-3.5" />Unlock</Button>
                  )}
                  {u.status === "approved" && (
                    <>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => resetPwd(u)} data-testid="admin-reset-pwd-btn"><KeyRound className="mr-1 h-3.5 w-3.5" />Reset password</Button>
                      <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={() => act(() => api.post(`/admin/users/${u.user_id}/deactivate`))} data-testid="admin-deactivate-btn"><Ban className="mr-1 h-3.5 w-3.5" />Deactivate</Button>
                    </>
                  )}
                  {(u.status === "deactivated" || u.status === "rejected") && (
                    <Button size="sm" variant="outline" className="h-8" onClick={() => act(() => api.post(`/admin/users/${u.user_id}/reactivate`))} data-testid="admin-reactivate-btn"><RotateCcw className="mr-1 h-3.5 w-3.5" />Reactivate</Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="fade-up">
      <PageHeader title="User Management" subtitle="Approve employee access requests and manage roles."
        action={
          <div className="flex items-center gap-2">
            {notifs.unread > 0 && (
              <Button variant="outline" onClick={markRead} data-testid="admin-mark-read">
                <Bell className="mr-1.5 h-4 w-4" />{notifs.unread} new request{notifs.unread > 1 ? "s" : ""}
              </Button>
            )}
            <Button onClick={() => setShowCreate(true)} data-testid="admin-add-employee-btn">
              <UserPlus className="mr-1.5 h-4 w-4" />Add Employee
            </Button>
          </div>
        } />

      <Tabs defaultValue="pending">
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="admin-tab-pending">
            Pending {pending.length > 0 && <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] text-white">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="admin-tab-all">All Users</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="pending"><UserTable rows={pending} /></TabsContent>
        <TabsContent value="all"><UserTable rows={users} /></TabsContent>
        <TabsContent value="activity">
          <Card className="p-6">
            {notifs.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity yet.</p>
            ) : (
              <div className="space-y-3">
                {notifs.items.map((n) => (
                  <div key={n.id} className="flex items-start gap-3 border-b border-border pb-3 last:border-0">
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-primary"><UserCog className="h-4 w-4" /></div>
                    <div>
                      <p className="text-sm">{n.message}</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(n.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!approveFor} onOpenChange={() => setApproveFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Approve {approveFor?.name}</DialogTitle>
            <DialogDescription>Grant this employee access and assign their role.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Assign a role for this employee. They requested <b className="capitalize">{(approveFor?.role_requested || "").replace(/_/g, " ")}</b>.</p>
          <div className="py-2">
            <Select value={roleSel} onValueChange={setRoleSel}>
              <SelectTrigger data-testid="approve-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveFor(null)}>Cancel</Button>
            <Button onClick={confirmApprove} data-testid="confirm-approve-btn">Approve & grant access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Add employee</DialogTitle>
            <DialogDescription>Create an approved account. We'll generate a temporary password and email it to them — they'll set their own on first login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="ce-name">Full name</Label>
              <Input id="ce-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="mt-1.5" data-testid="create-name" />
            </div>
            <div>
              <Label htmlFor="ce-email">Work email</Label>
              <Input id="ce-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="mt-1.5" data-testid="create-email" />
            </div>
            <div>
              <Label htmlFor="ce-phone">Phone (optional)</Label>
              <Input id="ce-phone" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className="mt-1.5" data-testid="create-phone" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="create-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createEmployee} disabled={creating} data-testid="create-submit">{creating ? "Creating…" : "Create account"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempResult} onOpenChange={() => setTempResult(null)}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Temporary password</DialogTitle>
            <DialogDescription>Share this with {tempResult?.name} ({tempResult?.email}). We also emailed it to them. They must change it on first login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {tempResult?.employee_id && (
              <p className="text-sm text-muted-foreground">Employee ID: <b className="font-mono">{tempResult.employee_id}</b></p>
            )}
            <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/50 px-4 py-3">
              <code className="flex-1 font-mono text-lg font-bold text-primary" data-testid="temp-password-value">{tempResult?.temp_password}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(tempResult?.temp_password || ""); toast.success("Copied"); }} data-testid="temp-password-copy">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempResult(null)} data-testid="temp-password-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
